// Post-authentication redirect plumbing.
//
// Marketing CTAs (e.g. "engage Uno" on the home hero) carry a `?next=`
// target so that once the user finishes signing in or up they land where they
// were headed instead of the generic /app chooser. The value is threaded
// through the sign-in/sign-up flow, including the magic-link check-email
// detour for new devices, and validated here on the way out.
//
// Validation is an open-redirect guard: `next` must be a safe, internal SPA
// path. A raw `?next=` is attacker-controllable (anyone can craft a link), so
// we reject anything that could navigate off-site (`//host`, `http://...`,
// `javascript:`) and constrain the rest to the handful of authenticated
// destinations a post-auth redirect should ever reach.

/** Fallback when no (valid) `next` is supplied. The /app chooser. */
export const DEFAULT_POST_AUTH = '/app';

/** Internal paths a post-auth redirect is allowed to land on: an agent console
 *  (`/agent/:slug`), the first-run tour (`/agent-intro/:slug`), or the app
 *  chooser/settings (`/app`, `/app/account`, `/app/documents`).
 *
 *  `agent-intro` is listed EXPLICITLY rather than by loosening `agent` to a
 *  prefix: every entry must match a whole path segment, so `/agentstuff` and
 *  `/application` stay rejected, and the removed `/agent-info/:slug` stays
 *  rejected too. Widening this regex widens an open-redirect control — add a
 *  literal route, never a prefix or a wildcard. */
const ALLOWED_NEXT = /^\/(agent|agent-intro|app)(\/|$)/;

/** Whitespace (covers control chars like tab/newline) or backslash. */
const FORBIDDEN_CHARS = /[\s\\]/;

/**
 * Validate a candidate redirect target. Returns true when it is a safe
 * internal SPA route.
 *
 * Rejects: protocol-relative (`//evil.com`, `/\evil.com`), absolute URLs
 * (`http://...`), scheme-bearing values (`javascript:...`), values with
 * whitespace/backslashes, and any path outside the allowlist.
 */
export function isSafeNext(value: string | null | undefined): value is string {
  if (!value) {
    return false;
  }
  // Must be a single-slash-rooted path; `//host` and `/\host` are
  // protocol-relative escapes in browsers.
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return false;
  }
  if (FORBIDDEN_CHARS.test(value)) {
    return false;
  }
  // No URL scheme (`javascript:`, `data:`, `http:`...).
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    return false;
  }
  return ALLOWED_NEXT.test(value);
}

/** Parse and validate `?next=` from a location.search string. */
export function readNext(search: string): string | null {
  const raw = new URLSearchParams(search).get('next');
  return isSafeNext(raw) ? raw : null;
}

/** Build `path?next=<encoded target>`, given an already-internal target. */
export function withNext(path: string, next: string): string {
  return `${path}?next=${encodeURIComponent(next)}`;
}
